import { useEffect, useState } from 'react';
import { FileText, Scissors, Sparkles, BookOpen, Image, MessageSquare } from 'lucide-react';
import { AdminLayout } from '../components/layout/AdminLayout';
import { api } from '../services/api';

interface Stats {
  pages: number;
  massages: number;
  soins: number;
  articles: number;
  testimonials: number;
  media: number;
}

export default function Dashboard() {
  const [stats, setStats] = useState<Stats | null>(null);

  useEffect(() => {
    api.get('/api/admin/stats')
      .then((res) => setStats(res.data))
      .catch(() => {});
  }, []);

  const cards = [
    { name: 'Pages', value: stats?.pages, icon: FileText, href: '/pages', color: 'bg-blue-500' },
    { name: 'Massages', value: stats?.massages, icon: Scissors, href: '/massages', color: 'bg-purple-500' },
    { name: 'Soins', value: stats?.soins, icon: Sparkles, href: '/soins', color: 'bg-pink-500' },
    { name: 'Articles', value: stats?.articles, icon: BookOpen, href: '/articles', color: 'bg-green-500' },
    { name: 'Témoignages', value: stats?.testimonials, icon: MessageSquare, href: '/testimonials', color: 'bg-amber-500' },
    { name: 'Médias', value: stats?.media, icon: Image, href: '/media', color: 'bg-cyan-500' },
  ];

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Welcome section */}
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Dashboard</h1>
          <p className="mt-1 sm:mt-2 text-sm sm:text-base text-gray-600">
            Bienvenue dans votre espace d'administration
          </p>
        </div>

        {/* Stats grid */}
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {cards.map((card) => (
            <a
              key={card.name}
              href={card.href}
              className="block bg-white rounded-lg shadow-sm p-4 sm:p-6 hover:shadow-md transition-shadow border border-gray-200"
            >
              <div className="flex items-center">
                <div className={`${card.color} p-3 rounded-lg`}>
                  <card.icon className="h-6 w-6 text-white" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">{card.name}</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {stats ? card.value : '—'}
                  </p>
                </div>
              </div>
            </a>
          ))}
        </div>

        {/* Quick actions */}
        <div className="bg-white rounded-lg shadow-sm p-4 sm:p-6 border border-gray-200">
          <h2 className="text-lg sm:text-xl font-semibold text-gray-900 mb-4">
            Actions rapides
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4">
            <a
              href="/pages"
              className="flex items-center p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <FileText className="h-5 w-5 text-gray-400 mr-3" />
              <div>
                <p className="font-medium text-gray-900">Gérer les pages</p>
                <p className="text-sm text-gray-500">Modifier le contenu des pages du site</p>
              </div>
            </a>
            <a
              href="/massages"
              className="flex items-center p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <Scissors className="h-5 w-5 text-gray-400 mr-3" />
              <div>
                <p className="font-medium text-gray-900">Ajouter un massage</p>
                <p className="text-sm text-gray-500">Créer une nouvelle prestation de massage</p>
              </div>
            </a>
            <a
              href="/soins"
              className="flex items-center p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <Sparkles className="h-5 w-5 text-gray-400 mr-3" />
              <div>
                <p className="font-medium text-gray-900">Ajouter un soin</p>
                <p className="text-sm text-gray-500">Créer un nouveau soin énergétique</p>
              </div>
            </a>
            <a
              href="/articles"
              className="flex items-center p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <BookOpen className="h-5 w-5 text-gray-400 mr-3" />
              <div>
                <p className="font-medium text-gray-900">Rédiger un article</p>
                <p className="text-sm text-gray-500">Publier un nouvel article de blog</p>
              </div>
            </a>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}
